import { A11y, Keyboard, Mousewheel, Pagination, Scrollbar } from "swiper";
import { useMemo, useState } from "react";
import { configColor, getPathByLink } from "@/utils";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import { useDataCard, useIsMobile, useModule } from "../../hooks";
import {
  IEventLaunchApiResponse,
  INewsCardApiResponse,
} from "../../interfaces/IEventLaunchDTO";
import { NewsCard } from "../Card";
import { useTranslation } from "react-i18next";
import { isWithinInterval, isValid } from "date-fns";

import DOMPurify from "isomorphic-dompurify";
import ProgressBar from "@ramonak/react-progress-bar";
import ModuleComponentProps from "../../interfaces/ModuleComponent";

import "swiper/css";
import "swiper/css/pagination";

const carouselAutoplayTime =
  process.env.NEXT_PUBLIC_CAROUSEL_AUTOPLAY_MILLISECONDS &&
  !isNaN(Number(process.env.NEXT_PUBLIC_CAROUSEL_AUTOPLAY_MILLISECONDS))
    ? Number(process.env.NEXT_PUBLIC_CAROUSEL_AUTOPLAY_MILLISECONDS)
    : 3000;

const EventLaunch: React.FC<ModuleComponentProps> = ({
  data,
  language,
}: ModuleComponentProps) => {
  const moduleData = data as IEventLaunchApiResponse;
  const { i18n } = useTranslation();
  const lang = language ? language : i18n.language;

  const [isMobile] = useIsMobile();
  const [, setActiveIndex] = useState(0);

  /** ---------------- FILTRO EVENTI ---------------- */
  const lstLaunch =
    moduleData?.data?.lstLaunch?.filter((launch) => {
      let isActive = false;
      let isValidAirport = false;

      try {
        const normalize = (x: string) => x?.replace(/\./g, ":");
        const start = new Date(`${launch.dteStart} ${normalize(launch.lblStartTime)}`);
        const end = new Date(`${launch.dteEnd} ${normalize(launch.lblEndTime)}`);
        const now = new Date();
        isActive =
          isValid(start) && isValid(end) && start <= end
            ? isWithinInterval(now, { start, end })
            : true;
      } catch {
        isActive = true;
      }

      try {
        const env = process.env.NEXT_PUBLIC_AIRPORT_REFERENCE?.toLowerCase()?.trim();
        if (!env) isValidAirport = true;
        else if (!launch?.xtaxAirports?.ti?.length) isValidAirport = true;
        else {
          isValidAirport = launch.xtaxAirports.ti.some((ti) => {
            const lbl = ti.label?.toLowerCase()?.trim();
            return !lbl || lbl === env;
          });
        }
      } catch {
        isValidAirport = true;
      }

      return isActive && isValidAirport;
    }) || [];

  const finalLstLaunch =
    lstLaunch.length > 0 ? lstLaunch : moduleData?.data?.lstLaunch?.slice(0, 1);

  /** ---------------- NEWS ---------------- */
  const url =
    process.env.NEXT_PUBLIC_BE_BASE_PATH +
    "/rest/v1/SEA-LinMxp/contents/news?query=enuPageCategory=%27News%27";

  const cardUrlList = useMemo(() => {
    return moduleData?.data.lstNews?.map((item) => item?.xconNews?.targeturl) || [];
  }, [moduleData]);

  let automatismoNewsData = useModule<any>(url);

  if (
    moduleData?.data?.enuTypeInfo === "automatismo news" &&
    !moduleData?.data?.lstAutomatismNews[0].flgAllNews
  ) {
    automatismoNewsData = automatismoNewsData?.data?.filter((news: any) => {
      return (
        moduleData?.data?.lstAutomatismNews[0]?.xtaxNewsCategory?.ti[0]?.label ===
        news?.xtaxNewsCategory?.ti[0]?.label
      );
    });
  }

  const selezioneNewsData = useDataCard<INewsCardApiResponse[]>({
    urls: cardUrlList,
  });

  const newsData =
    moduleData?.data?.enuTypeInfo === "automatismo news"
      ? moduleData?.data?.lstAutomatismNews[0].flgAllNews
        ? automatismoNewsData?.data
        : automatismoNewsData
      : selezioneNewsData;

  const textColor = moduleData?.data?.enuTitleColor
    ? configColor("text", moduleData?.data?.enuTitleColor)
    : "text-black";

  /** ---------------- RENDER ---------------- */
  return (
    <div className="px-6 max-w-cont-2 mt-8 mx-auto w-full py-8 desk:py-12 flex flex-col desk:flex-row desk:h-[35rem]">
      
      {/* --- LEFT --- */}
      <div className="rounded-2xl desk:cursor-pointer h-full desk:w-3/5 desk:mr-[10px]">

        {finalLstLaunch?.length > 1 ? (
          <Swiper
            onRealIndexChange={(instance) => setActiveIndex(instance.realIndex)}
            slidesPerView={1}
            spaceBetween={20}
            autoplay={{ delay: carouselAutoplayTime }}
            className="w-full h-full"
            wrapperClass="h-[90%]"
          >
            {finalLstLaunch.map((launch, index) => {
              const img = isMobile
                ? launch?.upiImageMobile?.[lang]
                : launch?.upiImageDesktop?.[lang];

              const imagePath = img.mediaserverpath + "/" + img.resourcename;
              const lbl = launch.lblLabelCta[lang];
              const color = launch.enuBackgroundCta;

              return (
                <SwiperSlide key={index} className="!w-full">
                  <div className="w-full h-full relative rounded-2xl overflow-hidden">
                    <a
                      href={getPathByLink(launch, lang)}
                      target={launch.flgTarget ? "_blank" : "_self"}
                    >
                      <img
                        alt={lbl}
                        src={imagePath}
                        className="rounded-2xl w-full h-auto object-cover"
                      />

                      <div className="absolute left-3 bottom-3">
                        <h1
                          className={`text-xl desk:text-xl font-semibold mb-2.5 font-secondary ${
                            color === "nero" ? "text-black" : "text-white"
                          }`}
                        >
                          {lbl}
                        </h1>
                      </div>
                    </a>
                  </div>
                </SwiperSlide>
              );
            })}

            <div className="flex justify-center">
              <div className="w-[35%] mt-6 mb-5">
                <Progress />
              </div>
            </div>
          </Swiper>
        ) : (
          finalLstLaunch?.map((item, index) => {
            const img = isMobile
              ? item?.upiImageMobile?.[lang]
              : item?.upiImageDesktop?.[lang];

            const imagePath = img.mediaserverpath + "/" + img.resourcename;
            const lbl = item.lblLabelCta[lang];
            const color = item.enuBackgroundCta;

            return (
              <div key={index} className="w-full h-full relative rounded-2xl overflow-hidden">
                <a
                  href={getPathByLink(finalLstLaunch[0], lang)}
                  target={finalLstLaunch[0].flgTarget ? "_blank" : "_self"}
                >
                  <img
                    alt={lbl}
                    src={imagePath}
                    className="rounded-2xl w-full h-auto object-cover"
                  />

                  <div className="absolute left-3 bottom-3">
                    <h1
                      className={`text-xl desk:text-xl font-semibold mb-2.5 font-secondary ${
                        color === "nero" ? "text-black" : "text-white"
                      }`}
                    >
                      {lbl}
                    </h1>
                  </div>
                </a>
              </div>
            );
          })
        )}
      </div>

      {/* --- RIGHT --- */}
      <div className={`h-full desk:w-2/5 ml-[10px] ${textColor}`}>
        <div className="mb-4 h-1/6">
          <h1
            className="mb-2 font-bold text-3xl font-secondary"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(moduleData?.data?.lblTitle?.[lang] || "")
            }}
          />
          <h2
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(moduleData?.data?.lblSubtitle?.[lang] || "")
            }}
          />
        </div>

        <div className="desk:h-[80%] desk:mt-15">
          <Swiper
            onRealIndexChange={(i) => setActiveIndex(i.realIndex)}
            direction={isMobile ? "horizontal" : "vertical"}
            modules={[Mousewheel, Keyboard, Pagination]}
            spaceBetween={20}
            slidesPerView={1}
            className="h-full"
            mousewheel
            keyboard={{ enabled: true }}
            pagination={
              !isMobile
                ? {
                    enabled: true,
                    clickable: true,
                    horizontalClass: "swiper-pagination-vertical-news",
                    dynamicBullets: true,
                    el: ".vertical-pagination-wrapper-news",
                    renderBullet: (i, className) =>
                      `<div class="${className} customBullet">${i}</div>`
                  }
                : false
            }
          >
            {newsData?.map((news: any, index: number) =>
              news?.data?.lblTitleNews?.[lang] ? (
                <SwiperSlide key={index} className="!h-full">
                  <NewsCard
                    data={
                      moduleData?.data?.enuTypeInfo === "automatismo news"
                        ? news
                        : news.data
                    }
                    lang={lang}
                    isMobile={isMobile}
                  />
                </SwiperSlide>
              ) : null
            )}

            {isMobile && (
              <div className="flex justify-center">
                <div className="w-[50%] mt-10">
                  <Progress />
                </div>
              </div>
            )}

            <div className="vertical-pagination-wrapper-news"></div>
          </Swiper>
        </div>
      </div>
    </div>
  );
};

const Progress: React.FC = () => {
  const swiperRef = useSwiper();
  return swiperRef.slides.length > 1 ? (
    <ProgressBar
      completed={swiperRef.activeIndex + 1}
      maxCompleted={swiperRef.slides.length}
      bgColor="#000"
      baseBgColor="#D3D3D3"
      isLabelVisible={false}
      height="5px"
    />
  ) : null;
};

export default EventLaunch;
